import React, { Component } from "react"
import PropTypes from "prop-types"
import isEqual from "lodash/isEqual"
import Highcharts from "highcharts/highstock"
import Exporting from "highcharts/modules/exporting"
import drilldown from "highcharts/modules/drilldown"
import { translate } from "react-i18next"
import { CHARTS } from "../../../constants"
import { getDrilldownSeries, getDrillDownRootSeries } from "../../../utils/drilldown-utils"
import { formatDecimalForDisplay } from "../../../utils/formatting-utils"
import DrilldownShape from "../../../state/shapes/DrilldownShapes/DrilldownShape"

class DrilldownColumnChart extends Component {
  state = {
    hasAutoDrilledDownAlready: false
  }

  componentDidMount = () => {
    this.createChart()
  }

  shouldComponentUpdate = nextProps => {
    return (
      (nextProps.chartDrilldownLevel === 0 && nextProps.isPerformanceComparison === false) ||
      !isEqual(this.props.data, nextProps.data) ||
      !isEqual(this.props.yAxisTitle, nextProps.yAxisTitle)
    )
  }

  componentDidUpdate = () => {
    if (this.props.allowUpdate) {
      this.createChart()
    }
    if (this.props.onComponentUpdate) {
      this.props.onComponentUpdate()
    }
  }

  componentWillUnmount = () => {
    this.chart.destroy()
  }

  createChart = () => {
    drilldown(Highcharts)

    // Get the element into which to insert the chart
    const chartContainer = this.chartContainerRef

    const {
      t,
      yAxisTitle,
      data,
      scrollbarEnabled,
      onDrillDown,
      onDrillUp,
      yAxisMaxValue,
      xAxisTitle,
      xAxisFormatter,
      tooltipFormatter,
      dataLabelFormatter,
      chartDrilldownLevel,
      isPerformanceComparison,
      titleKey,
      subtitleKey
    } = this.props

    Exporting(Highcharts)
    // Null as widget titleKey describes chart
    const chartTitle = null

    const seriesData = getDrillDownRootSeries(data, t)
    const drilldownData = getDrilldownSeries(data, t)

    const numberOfColumnsBeforeScroll = 15

    /** Updates the scrollbar and max count
     * Relies on HighStock as that's needed for scrollbars in Highcharts 💩
     * If the max is too high, it'll display empty spaces on the right hand side
     */
    const updateScrollAxis = (chart, columnCount) => {
      const xAxisMax = scrollbarEnabled
        ? Math.min(numberOfColumnsBeforeScroll - 1, columnCount - 1)
        : undefined
      const enableScroll = scrollbarEnabled && columnCount > numberOfColumnsBeforeScroll

      chart.xAxis[0].update({
        max: xAxisMax,
        scrollbar: {
          enabled: enableScroll
        }
      })
    }

    this.chart = new Highcharts.Chart(
      {
        chart: {
          animation: false,
          renderTo: chartContainer,
          height: CHARTS.standardHeight - 35,
          type: "column",
          events: {
            drillup(e) {
              updateScrollAxis(this, e.seriesOptions.data.length)
              if (onDrillUp) {
                onDrillUp(e, this)
              }
            },
            drilldown(e) {
              // prettier-ignore-line
              updateScrollAxis(this, e.seriesOptions.data.length)
              if (onDrillDown) {
                onDrillDown(e, this)
              }
            }
          }
        },
        lang: {
          drillUpText: t("Back"),
          contextButtonTitle: t("Common.ChartDownloadOptions")
        },
        title: {
          text: titleKey != null && titleKey.length > 0 ? t(titleKey) : null,
          y: -30
        },
        subtitle: {
          text: subtitleKey != null && subtitleKey.length > 0 ? t(subtitleKey) : null,
          y: -15
        },
        colors: CHARTS.colours,
        xAxis: {
          type: "category",
          labels: {
            formatter() {
              if (xAxisFormatter) {
                return xAxisFormatter(this.value)
              }
              return this.value
            },
            autoRotation: [-10, -20, -30, -40, -50, -60, -70, -80, -90]
          },

          titleKey: {
            text: xAxisTitle ? t(xAxisTitle) : null,
          },
          min: scrollbarEnabled ? 0 : undefined,
          max: numberOfColumnsBeforeScroll,
          scrollbar: {
            enabled: false
          }
        },
        yAxis: {
          allowDecimals: false,
          ceiling: yAxisMaxValue,
          max: yAxisMaxValue,
          titleKey: {
            text: yAxisTitle ? t(yAxisTitle) : null,
          },
        },
        legend: {
          enabled: false
        },
        credits: {
          enabled: false
        },
        exporting: {
          enabled: CHARTS.exportEnabled,
          buttons: {
            contextButton: {
              menuItems: CHARTS.exportButtons,
            },
          },
          filename: titleKey != null && titleKey.length > 0 ? t(titleKey) : null,
          chartOptions: {
            plotOptions: {
              series: {
                dataLabels: {
                  enabled: true,
                  formatter() {
                    return this.total + ' (' + formatDecimalForDisplay(this.y) + '%)'
                  },
                  shadow: false,
                  color: "#333333",
                  style: {
                    fontWeight: "normal",
                    fontSize: "8px",
                    textOutline: false,
                  },
                },
              },
            },
            chart: {
              spacingTop: 50,
            },
          }
        },
        plotOptions: {
          series: {
            borderWidth: 0,
            cropThreshold: 2000,
            dataLabels: {
              enabled: true,
              formatter() {
                if (dataLabelFormatter) {
                  return dataLabelFormatter(this)
                }
                return `${this.total ? this.total : 0} \r\n (${formatDecimalForDisplay(this.y)}%) `
              }
            },
            minPointLength: 2
          }
        },
        tooltip: {
          formatter() {
            if (tooltipFormatter) {
              return tooltipFormatter(this)
            }

            return `<div><span style="font-size:11px"><b>${this.series.name}</b></span>
          <br><span style="color:${this.point.color}">${this.point.name}</span>: <b>${
              this.point.y
            }%</b> of total<br/>
          </div>`
          }
        },
        series: seriesData,
        drilldown: {
          animation: false,
          series: drilldownData,
          drillUpButton: {
            position: {
              x: 0,
              y: 25
            }
          }
        }
      },
      chart => {
        // Chart rendered callback
        if (seriesData && seriesData[0] && seriesData[0].data) {
          updateScrollAxis(chart, seriesData[0].data.length)
          if (
            seriesData[0].data.length === 1 && // If there's only one root item
            chartDrilldownLevel === 0 && // and we're currently at root level
            !this.state.hasAutoDrilledDownAlready && // and we've not already auto drilled down before
            !isPerformanceComparison
          ) {
            // Set `hasAutoDrilledDownAlready` to true to allow drilling up to the root item again
            // as the back button will always display
            this.setState({ hasAutoDrilledDownAlready: true })

            // Do the actual drilldown
            chart.series[0].points[0].doDrilldown()
          }

          if (
            seriesData[0].data.length === 1 && // If there's only one root item
            chartDrilldownLevel === 0 && // and we're currently at root level
            isPerformanceComparison
          ) {
            // Do the actual drilldown
            chart.series[0].points[0].doDrilldown()
          }
        }
      }
    )
  }

  render() {
    // the inline-block 99% width div fixes is a hacky fix
    // for an issue where the full width chart forces the whole
    // dashboard to overlap the left hand filter bar when on a
    // Microsoft Surface sized screen
    return (
      <div style={{ display: "inline-block", width: "99%" }}>
        <div
          className="chart-container"
          ref={c => {
            this.chartContainerRef = c
          }}
        />
      </div>
    )
  }
}

DrilldownColumnChart.propTypes = {
  data: PropTypes.shape(DrilldownShape).isRequired,
  yAxisTitle: PropTypes.string.isRequired,
  scrollbarEnabled: PropTypes.bool.isRequired,
  chartDrilldownLevel: PropTypes.number.isRequired,
  onDrillDown: PropTypes.func,
  onDrillUp: PropTypes.func,
  allowUpdate: PropTypes.bool,
  yAxisMaxValue: PropTypes.number,
  xAxisTitle: PropTypes.string.isRequired,
  xAxisFormatter: PropTypes.func,
  tooltipFormatter: PropTypes.func,
  dataLabelFormatter: PropTypes.func,
  isPerformanceComparison: PropTypes.bool,
  titleKey: PropTypes.string,
  subtitleKey: PropTypes.string
}

DrilldownColumnChart.defaultProps = {
  onDrillDown: null,
  onDrillUp: null,
  allowUpdate: true,
  yAxisMaxValue: undefined,
  tooltipFormatter: null,
  xAxisFormatter: null,
  dataLabelFormatter: null,
  isPerformanceComparison: false,
  titleKey: '',
  subtitleKey: '',
  onComponentUpdate: null
}

export default translate()(DrilldownColumnChart)



// WEBPACK FOOTER //
// ./src/components/charts/generic/DrilldownColumnChart.jsx